<script lang="ts">
    import { onMount } from "svelte"
    import { kirbyFetch } from "../api/kirby/api"
    import { topicQuery } from "../api/kirby/queries"
    import { correctI18nPath } from "../functionTools/routing"
    import { localePattern } from "../i18n"
    import {
        eventlocations,
        eventseries,
        isHeroVisible,
        lang,
        languageSwitching,
        location,
        topics,
        topicsLoaded,
    } from "../store"
    import Footer from "./layout/global/Footer.svelte"
    import Header from "./layout/global/header/Header.svelte"
    import ContentPage from "./routes/ContentPage.svelte"
    import Homepage from "./routes/Homepage.svelte"
    import Search from "./routes/Search.svelte"
    import MiniLoader from "./widgets/MiniLoader.svelte"
    import ScrollTop from "./widgets/ScrollTop.svelte"
    import SidebarOverlay from "./widgets/SidebarOverlay.svelte"
    import CookieFingerprint from "./widgets/CookieFingerprint.svelte"
    import { baseURL } from "../configs/config"

    interface Props {
        url?: string
    }

    let { url = "" }: Props = $props()
    const isServer = typeof window === "undefined"
    if (url) {
        // ssr
        let l = url.split("?")
        const actualURL = baseURL + url
        $location = {
            url: actualURL,
            path: l[0],
            search: l.length > 1 ? l[1] : "",
            hash: "",
            push: false,
            pop: false,
            categoryPath: l[0].replace(/\/[^_\/]+_[^\/]+$/, ""),
        }
        // Sprache aus URL ableiten (SSR: window existiert nicht, daher $lang manuell setzen)
        $lang = l[0].startsWith("/en") ? "en" : "de"
    }

    async function loadTopics() {
        const topicsPages = await kirbyFetch<Topic[]>({
            query: "site.index.filterBy('intendedTemplate', 'topics')",
            select: topicQuery,
        })
        for (const page of topicsPages) {
            $topics[page.slug] = {
                title: page.title,
                slug: page.slug,
                uuid: page.uuid,
                primary_image: page.primary_image,
                hover_image: page.hover_image,
            }
        }
        $topicsLoaded = true
    }

    async function loadEventseries() {
        const eventseriesPages = await kirbyFetch<EventSeries[]>({
            query: "site.index.filterBy('intendedTemplate', 'eventseries')",
            select: {
                title: true,
                uuid: true,
                slug: true,
            },
        })
        for (const page of eventseriesPages) {
            $eventseries[page.slug] = {
                title: page.title,
                uuid: page.uuid,
                slug: page.slug,
            }
        }
    }

    async function loadEventlocations() {
        const eventLocationPages = await kirbyFetch<EventLocation[]>({
            query: "site.index.filterBy('intendedTemplate', 'eventlocation')",
            select: {
                title: true,
                uuid: true,
                slug: true,
            },
        })
        if (!eventLocationPages) return
        for (const page of eventLocationPages) {
            $eventlocations[page.slug] = {
                title: page.title,
                uuid: page.uuid,
                slug: page.slug,
            }
        }
    }

    if (isServer) {
        void loadEventseries()
        void loadEventlocations()
        if (!$topicsLoaded) void loadTopics()
    }

    onMount(() => {
        const currentPath = window.location.pathname || "/"
        const hasLocalePrefix = /^\/(de|en)(\/|$)/.test(currentPath)
        if (!hasLocalePrefix && currentPath !== "/") {
            correctI18nPath(currentPath)
            return
        }
        void loadEventseries()
        void loadEventlocations()
        if (!$topicsLoaded) void loadTopics()
    })
</script>

<div class="t-sec-layout">
    {#if !$isHeroVisible}
        <Header type="white" position="sticky" />
    {/if}
    <div class="content-w">
        {#if $languageSwitching}
            <MiniLoader />
        {:else if $location.path == `/${$lang}` || $location.path == `/${$lang}/` || $location.path == `/${$lang}/home` || $location.path == `/${$lang}/home/`}
            <Homepage />
        {:else if $location.path == `/${$lang}/search`}
            <Search />
        {:else}
            {#key $location.path}
                <ContentPage path={$location.path} />
            {/key}
        {/if}
    </div>
    <Footer />
</div>
<ScrollTop />
<SidebarOverlay />
<CookieFingerprint />

<style lang="less" global>
    @import "../css/main.less";
    @import "../css/webfont.less";
    @import "../css/variables.less";
    #appContainer {
        min-height: 100vh;
    }
    .t-sec-layout {
        display: flex;
        min-height: 100vh;
        flex-direction: column;
        justify-content: space-between;
    }
    .content-w {
        flex-grow: 1;
    }
    main.page,
    main.contentpage {
        width: 100%;
        height: 100%;
        background-color: var(--bg-100) !important;
        display: flex;
        flex-direction: column;
        position: relative;
        z-index: 5;
        justify-content: flex-start;
        border-radius: 40px 40px 0px 0px !important;
        padding-top: 60px;
        @media @max-mobile {
            border-radius: 20px 20px 0px 0px !important;
        }
    }
</style>
